import {
  AcademicCapIcon,
  CheckCircleIcon,
  StopIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';

import { useTutorial } from '@/components/tutorial/TutorialProvider';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const COMPLETED_DISPLAY_MS = 8_000;

export function TutorialCard() {
  const { isActive, currentStep, totalSteps, triggeredBy, dismissTutorial } =
    useTutorial();
  const [justCompleted, setJustCompleted] = useState(false);

  useEffect(() => {
    function handleCompleted(e: Event) {
      const detail = (e as CustomEvent).detail as
        | { triggeredBy?: string }
        | undefined;
      if (detail?.triggeredBy === 'marcus') {
        setJustCompleted(true);
      }
    }
    window.addEventListener('tutorial-completed', handleCompleted);
    return () =>
      window.removeEventListener('tutorial-completed', handleCompleted);
  }, []);

  useEffect(() => {
    if (!justCompleted) return;
    const t = setTimeout(() => setJustCompleted(false), COMPLETED_DISPLAY_MS);
    return () => clearTimeout(t);
  }, [justCompleted]);

  if (justCompleted) {
    return (
      <div
        data-testid="tutorial-completed-card"
        className="border-chariot-stroke/20 bg-chariot-layer2 my-2 flex items-center gap-3 rounded-lg border px-3 py-2"
      >
        <CheckCircleIcon className="size-5 shrink-0 text-green-500" />
        <span className="text-chariot-text-primary text-xs font-medium">
          Tutorial completed
        </span>
      </div>
    );
  }

  if (!isActive || triggeredBy !== 'marcus') return null;

  const progress = ((currentStep + 1) / totalSteps) * 100;

  return (
    <div
      data-testid="tutorial-active-card"
      className="border-chariot-stroke/20 bg-chariot-layer2 my-2 flex items-center gap-3 rounded-lg border px-3 py-2"
    >
      <AcademicCapIcon className="text-chariot-brand-primary size-5 shrink-0" />
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <span className="text-chariot-text-primary text-xs font-medium">
          Tutorial running
        </span>
        <span className="text-chariot-text-secondary text-xs">
          Step {currentStep + 1} of {totalSteps}
        </span>
        <div className="bg-chariot-stroke/30 h-1 flex-1 overflow-hidden rounded-full">
          <div
            className={cn(
              'bg-chariot-brand-primary h-full rounded-full transition-all duration-300'
            )}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="text-chariot-text-secondary hover:text-chariot-text-primary h-6 gap-1 px-2 text-xs"
        onClick={dismissTutorial}
      >
        <StopIcon className="size-3" />
        Stop
      </Button>
    </div>
  );
}
